import React, { useEffect, useState } from 'react'
import { EcomDataLayerHub } from '../../javascript/analytics/ecommerceDataLayerEventHub.services'
import { getProgramsWithCompareDetails, resolveUrl } from '../helpers/helpers'
import { IEpiProps, IEventRegistrationRefreshModel, ISpecialtyProgram, IUserSelections, StepConstants } from '../models/event-reg-refresh-models'
import { useEventRegRefreshContext, useEventRegRefreshDispatch } from '../reducers/event-reg-refresh-reducers'
import { CompareTool } from './compare-tool'
import { ContentWrapper } from './layout/content-wrapper'
import { MainContent } from './layout/main-content'
import { SideBarContent } from './layout/sidebar-content'
import { Modal } from './modal'
import { ProgramCard } from './program-card'
import { ProgramFilters } from './program-filters'
import { ProgramSummary } from './program-summary'
import { RenewalEligibility } from './renewal-eligibility'


interface IContextProps {
  epiProps: IEpiProps
  eventRegistrationRefreshModel: IEventRegistrationRefreshModel
  currentStep: string
  userSelections: IUserSelections
}

export const ProgramStep = () => {
  const { epiProps, eventRegistrationRefreshModel, currentStep, userSelections }: IContextProps = useEventRegRefreshContext()!
  const dispatch = useEventRegRefreshDispatch()!

  const [showRenewalPrograms, setShowRenewalPrograms] = useState(false);
  const [showValidationMessage, setShowValidationMessage] = useState(false);
  const [specialtyToDisplay, setSpecialtyToDisplay] = useState(""); //Used if query string is present to narrow down to 1 specialty.
  const [programsToDisplay, setProgramsToDisplay] = useState<ISpecialtyProgram[]>([])
  const [showingSpecialtyView, setShowingSpecialtyView] = useState(false);
  const [totalProgramCount, setTotalProgramCount] = useState(0);
  const [specialtyCodeToCheckRenewalEligibilityFor, setSpecialtyCodeToCheckRenewalEligibilityFor] = useState("");
  const [showDirectDeliveryPrograms, setShowDirectDeliveryPrograms] = useState(false);
  const [allProgramsForCurrentToggle, setAllProgramsForCurrentToggle] = useState<ISpecialtyProgram[]>([]);

  // Filter state
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([])
  const [selectedProgramFormats, setSelectedProgramFormats] = useState<string[]>([])
  const [selectedTrainingTypes, setSelectedTrainingTypes] = useState<string[]>([])
  const [filtersApplied, setFiltersApplied] = useState(false)

  //Compare Tool
  const [showCompareTool, setShowCompareTool] = useState(false);

  //Get specialty code, renewal vs first time, and filters from query string if present
    useEffect(() => {
      if (currentStep == StepConstants.ProgramStep) {
            EcomDataLayerHub.viewProgramSelection(StepConstants.ProgramStep)

      }
  }, [currentStep])
  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);

    // Handle specialty parameter
    const specialtyParam = queryParams.get('specialty');
    if (specialtyParam) {
      setSpecialtyToDisplay(specialtyParam.toUpperCase()); // Convert to uppercase for case-insensitive comparison
      setShowingSpecialtyView(true);
    }

    // Handle programType parameter
    const programTypeParam = queryParams.get('programtype');
    const isDirectDelivery = programTypeParam?.toLowerCase() === 'directdelivery';

    if (programTypeParam) {
      if (isDirectDelivery) {
        setShowDirectDeliveryPrograms(true);
      } else if (programTypeParam.toLowerCase() === 'renewal') {
        setShowRenewalPrograms(true);
      } else if (programTypeParam.toLowerCase() === 'icp') {
        setShowRenewalPrograms(false);
      }
    }

    // Parse and apply filter parameters only if not showing specialty view or direct delivery view
    const filters = parseFiltersFromURL()
    const hasFilters = filters.industries.length > 0 || filters.programFormats.length > 0 || filters.trainingTypes.length > 0;

    // Only apply filters if not in specialty view and not in direct delivery view
    if (hasFilters && !specialtyParam && !isDirectDelivery) {
      setSelectedIndustries(filters.industries)
      setSelectedProgramFormats(filters.programFormats)
      setSelectedTrainingTypes(filters.trainingTypes)
      setFiltersApplied(true)
    }
  }, []);

  // Effect to sync renewal toggle state with selected program
  useEffect(() => {
    if (userSelections?.userProgramSelection?.programCode) {
      setShowRenewalPrograms(userSelections.userProgramSelection.isRenewal || false);
    }
  }, [userSelections]);

  // Effect to update program display whenever filter/toggle/specialty state changes
  useEffect(() => {
    updateProgramDisplay();
  }, [
    specialtyToDisplay,
    showRenewalPrograms,
    showDirectDeliveryPrograms,
    eventRegistrationRefreshModel,
    filtersApplied,
    showingSpecialtyView
  ]);


  // Filter handler functions
  const handleIndustryFilterChange = (value: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedIndustries([...selectedIndustries, value])
    } else {
      setSelectedIndustries(selectedIndustries.filter(v => v !== value))
    }
  }

  const handleProgramFormatFilterChange = (value: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedProgramFormats([...selectedProgramFormats, value])
    } else {
      setSelectedProgramFormats(selectedProgramFormats.filter(v => v !== value))
    }
  }

  const handleTrainingTypeFilterChange = (value: string, isChecked: boolean) => {
    if (isChecked) {
      setSelectedTrainingTypes([...selectedTrainingTypes, value])
    } else {
      setSelectedTrainingTypes(selectedTrainingTypes.filter(v => v !== value))
    }
  }

  const handleApplyFilters = () => {
    // Only set filtersApplied to true if there are actually filters to apply
    const hasFilters = selectedIndustries.length > 0 ||
      selectedProgramFormats.length > 0 ||
      selectedTrainingTypes.length > 0

    setFiltersApplied(hasFilters)
    EcomDataLayerHub.filterPrograms(
      'Industries: ' + selectedIndustries,
      'Delivery Method: ' + selectedProgramFormats,
      'Training Type: ' + selectedTrainingTypes,
    )
    updateURLWithFilters(selectedIndustries, selectedProgramFormats, selectedTrainingTypes)
    updateProgramDisplay()
  }

  const handleClearAllFilters = () => {
    setSelectedIndustries([])
    setSelectedProgramFormats([])
    setSelectedTrainingTypes([])
    setFiltersApplied(false)

    // Clear filter params from URL
    const url = new URL(window.location.href)
    url.searchParams.delete('industries')
    url.searchParams.delete('programFormats')
    url.searchParams.delete('trainingTypes')
    window.history.replaceState({}, '', url.toString())

    updateProgramDisplay()
  }

  // Update URL with filter parameters
  const updateURLWithFilters = (industries: string[], formats: string[], types: string[]) => {
    const url = new URL(window.location.href)

    // Update or remove industries
    if (industries.length > 0) {
      url.searchParams.set('industries', industries.join(','))
    } else {
      url.searchParams.delete('industries')
    }

    // Update or remove programFormats
    if (formats.length > 0) {
      url.searchParams.set('programFormats', formats.join(','))
    } else {
      url.searchParams.delete('programFormats')
    }

    // Update or remove trainingTypes
    if (types.length > 0) {
      url.searchParams.set('trainingTypes', types.join(','))
    } else {
      url.searchParams.delete('trainingTypes')
    }

    window.history.replaceState({}, '', url.toString())
  }

  // Parse filter parameters from URL
  const parseFiltersFromURL = () => {
    const queryParams = new URLSearchParams(window.location.search)

    const industries = queryParams.get('industries')
      ? queryParams.get('industries')!.split(',').map(s => s.trim())
      : []

    const programFormats = queryParams.get('programFormats')
      ? queryParams.get('programFormats')!.split(',').map(s => s.trim())
      : []

    const trainingTypes = queryParams.get('trainingTypes')
      ? queryParams.get('trainingTypes')!.split(',').map(s => s.trim())
      : []

    return { industries, programFormats, trainingTypes }
  }

  // Function to update the displayed programs based on current selection (renewal vs toggle, query string)
  const updateProgramDisplay = () => {
    if (!eventRegistrationRefreshModel || !eventRegistrationRefreshModel.specialtyPrograms) {
      setProgramsToDisplay([]);
      setTotalProgramCount(0);
      setAllProgramsForCurrentToggle([]);
      return;
    }

    let programsForToggle = eventRegistrationRefreshModel.specialtyPrograms;

    // Show direct delivery programs if in direct delivery mode
    if (showDirectDeliveryPrograms) {
      programsForToggle = programsForToggle.filter(
        (program: ISpecialtyProgram) => program.isDirectDelivery === true
      );
    } else {
      // Get all programs matching the renewal status, excluding direct delivery programs
      programsForToggle = programsForToggle.filter(
        (program: ISpecialtyProgram) => program.isRenewal === showRenewalPrograms && program.isDirectDelivery !== true
      );
    }

    // Store programs for current toggle
    setAllProgramsForCurrentToggle(programsForToggle);

    // Save the total count for display
    setTotalProgramCount(programsForToggle.length)

    let filteredPrograms = programsForToggle
    if (filtersApplied) {
      filteredPrograms = programsForToggle.filter((program: ISpecialtyProgram) => {
        if (selectedIndustries.length > 0) {
          const hasMatchingIndustry = program.industries?.some(ind =>
            selectedIndustries.includes(ind)
          )
          if (!hasMatchingIndustry) return false
        }

        if (selectedProgramFormats.length > 0) {
          const hasMatchingFormat = program.programFormats?.some(pf =>
            selectedProgramFormats.includes(pf)
          )
          if (!hasMatchingFormat) return false
        }

        if (selectedTrainingTypes.length > 0) {
          const hasMatchingType = program.trainingTypes?.some(tt =>
            selectedTrainingTypes.includes(tt)
          )
          if (!hasMatchingType) return false
        }

        return true
      })
    }

    // Showing specialty view (one card only)
    if (specialtyToDisplay && showingSpecialtyView) {
      const matchingPrograms = programsForToggle.filter(
        (p: ISpecialtyProgram) => p.programCode.toUpperCase() === specialtyToDisplay
      );

      if (matchingPrograms.length > 0) {
        // Show just the matching program
        setProgramsToDisplay(matchingPrograms);
      } else {
        // No match found - show all programs
        setProgramsToDisplay(programsForToggle);
        setShowingSpecialtyView(false);
      }
    } else {
      // No speciality or showing all - display all programs
      setProgramsToDisplay(filteredPrograms);
    }
  };

  const handleProgramSelect = (program: ISpecialtyProgram) => {
    // Check if this program is currently selected
    const isCurrentlySelected =
      userSelections.userProgramSelection?.programCode === program.programCode &&
      userSelections.userProgramSelection?.isRenewal === program.isRenewal &&
      userSelections.userProgramSelection?.isDirectDelivery === program.isDirectDelivery;

    if (isCurrentlySelected) {
      // Deselect - clear the program selection
      dispatch({
        type: 'updateUserSelections',
        data: {
          userProgramSelection: null,
          userAddOnProgramSelections: [],
          userProgramDateTimeSelection: null,
          userAddOnProgramDateTimeSelection: null,
          attendees: []
        }
      });
    }
    else {
      dispatch({
        type: 'updateUserSelections',
        data: {
          userProgramSelection: {
            programName: program.programName,
            programCode: program.programCode,
            isRenewal: program.isRenewal,
            isDirectDelivery: program.isDirectDelivery
          },
          userAddOnProgramSelections: [],
          userProgramDateTimeSelection: null,
          userAddOnProgramDateTimeSelection: null,
          attendees: []
        }
      });
    }
    // Clear shared date/time events when specialty program changes
    dispatch({
      type: 'clearSharedProgramDateTimeEvents'
    });
    dispatch({
      type: 'resetStepsAfter',
      data: StepConstants.ProgramStep
    });

  }

  const handleContinue = () => {
    let hasSelectedProgram =
      userSelections.userProgramSelection &&
      userSelections.userProgramSelection.programName &&
      userSelections.userProgramSelection.programName.length > 0;

    if (hasSelectedProgram) {
      setShowValidationMessage(false);

      // Find the selected specialty program
      const selectedProgram = eventRegistrationRefreshModel.specialtyPrograms.find(
        p => p.programCode === userSelections.userProgramSelection.programCode
          && p.isRenewal === userSelections.userProgramSelection.isRenewal
          && p.isDirectDelivery === userSelections.userProgramSelection.isDirectDelivery
      );

      // Check if the program has add-ons available
      const hasAddOns = selectedProgram &&
        selectedProgram.addOnPrograms &&
        selectedProgram.addOnPrograms.length > 0;
        
        //select_program event
        EcomDataLayerHub.selectProgram(
          userSelections.userProgramSelection.programName,
          userSelections.userProgramSelection.programCode,
          userSelections.userProgramSelection.isDirectDelivery.toString(),
        )
      // Navigate to Add Ons step if add-ons are available, otherwise go to Date/Time step
      dispatch({
        type: 'updateCurrentStep',
        data: hasAddOns ? StepConstants.AddOnsStep : StepConstants.DateTimeSpecialtyProgramStep,
      });
    } else {
      setShowValidationMessage(true);
    }
  }

  const handleProgramTypeToggle = (isRenewal: boolean) => {
    setShowRenewalPrograms(isRenewal);
    setShowDirectDeliveryPrograms(false); //If the user selects first-time or renewal certifications and they were in DD mode, hide DD mode.

    // Reset filters when switching program type
    setSelectedIndustries([])
    setSelectedProgramFormats([])
    setSelectedTrainingTypes([])
    setFiltersApplied(false)

    // Update URL to reflect program type and clear filter params
    const url = new URL(window.location.href);
    url.searchParams.set('programtype', isRenewal ? 'Renewal' : 'ICP');
    url.searchParams.delete('industries')
    url.searchParams.delete('programFormats')
    url.searchParams.delete('trainingTypes')
    window.history.replaceState({}, '', url.toString());

    // useEffect will trigger updateProgramDisplay when showRenewalPrograms changes
  };

  // Handle "View All Programs" button click
  const handleViewAllPrograms = () => {
    setShowingSpecialtyView(false);
    setSpecialtyToDisplay("");
    setShowDirectDeliveryPrograms(false);

    updateProgramDisplay();

    // Update URL to remove specialty and direct delivery program type parameter
    const url = new URL(window.location.href);
    if (url.searchParams.has('specialty')) {
      url.searchParams.delete('specialty');
    }
    if (url.searchParams.has('programtype')) {
      url.searchParams.delete('programtype');
    }
    window.history.replaceState({}, '', url.toString());
  };

  const handleCheckRenewalEligibility = (programCode: string) => {
    setSpecialtyCodeToCheckRenewalEligibilityFor(programCode);
  }

  // Get the specialty program name for the modal title
  const getRenewalSpecialtyProgramName = (specialtyCode: string): string => {
    if (!eventRegistrationRefreshModel || !eventRegistrationRefreshModel.specialtyPrograms || !specialtyCode) {
      return '';
    }

    // Find the specialty program that matches the specialtyCode
    const matchingProgram = eventRegistrationRefreshModel.specialtyPrograms.find(
      program => program.programCode === specialtyCode && program.isRenewal == true
    );

    return matchingProgram ? matchingProgram.programName : specialtyCode;
  }

  const showCompareCta = (allProgramsForCurrentToggle: ISpecialtyProgram[]) => {
    const programsWithCompareDetails = getProgramsWithCompareDetails(allProgramsForCurrentToggle);
    // Only display compare tool if there are more than 2 programs to compare.
    return programsWithCompareDetails && programsWithCompareDetails.length > 2;
  }

  if (currentStep == StepConstants.ProgramStep) {
    return (
      <div>
        <section className="app-section-header">
          <div className="app-section-header-inner">
            <h1 className="app-section-title">{epiProps.selectProgramStepTitle}</h1>
            <p className="app-section-description">{epiProps.selectProgramStepDescription}</p>

            {/* Program Filters */}
            <div className="filters-program">
              <ProgramFilters
                programs={allProgramsForCurrentToggle}
                selectedFilters={{
                  industries: selectedIndustries,
                  programFormats: selectedProgramFormats,
                  trainingTypes: selectedTrainingTypes
                }}
                onIndustryChange={handleIndustryFilterChange}
                onProgramFormatChange={handleProgramFormatFilterChange}
                onTrainingTypeChange={handleTrainingTypeFilterChange}
                onApplyFilters={handleApplyFilters}
                showFilters={showingSpecialtyView === false && showDirectDeliveryPrograms === false}
              />

              {
                epiProps.showCompareProgramsCta === true && showCompareCta(allProgramsForCurrentToggle) &&
                <button
                  className="btn-tertiary"
                  onClick={() => setShowCompareTool(true)}>
                  {epiProps.compareProgramsCtaText}
                </button>
              }
            </div>

            <div className="app-sort">
              <p><strong>{epiProps.viewLabelFirstTimeCertificationVsRenewals}</strong></p>
              <div className="app-sort-actions">
                {showDirectDeliveryPrograms && (
                  <button className={`app-sort-button ${showDirectDeliveryPrograms ? "active" : ""}`}
                    onClick={() => { }}>
                    {epiProps.directDeliveryToggleText}
                  </button>
                )}
                <button className={`app-sort-button ${showRenewalPrograms == false && !showDirectDeliveryPrograms ? "active" : ""}`}
                  onClick={() => handleProgramTypeToggle(false)}>
                  {epiProps.firstTimeCertificationToggleText}
                </button>
                <button className={`app-sort-button ${showRenewalPrograms == true && !showDirectDeliveryPrograms ? "active" : ""}`}
                  onClick={() => handleProgramTypeToggle(true)}>
                  {epiProps.renewalsToggleText}
                </button>
              </div>
              <p><strong>{epiProps.showingNumberOfProgramsText
                ? epiProps.showingNumberOfProgramsText.replace('{0}', programsToDisplay.length.toString()).replace('{1}', totalProgramCount.toString())
                : `Showing ${programsToDisplay.length} of ${totalProgramCount} programs`}</strong></p>
            </div>
          </div>
        </section>
        <ContentWrapper>
          <MainContent>
            {/* Display no results message if filters applied and no programs match */}
            {filtersApplied && programsToDisplay.length === 0 && (
              <div className="stack-lg">
                {epiProps.noFilterResultsMessage && (
                  <div className="rich-text" dangerouslySetInnerHTML={{ __html: epiProps.noFilterResultsMessage }} />
                )}
                <div className='button-list'>
                  <button className="btn-primary button-list-item" onClick={handleClearAllFilters}>
                    {epiProps.clearFiltersButtonText}
                  </button>
                  {epiProps.noFilterResultsCtaButton && epiProps.noFilterResultsCtaButton.href && (
                    <a
                      href={resolveUrl(epiProps.noFilterResultsCtaButton.href)}
                      target={epiProps.noFilterResultsCtaButton.target}
                      className="btn-tertiary button-list-item"
                      title={epiProps.noFilterResultsCtaButton.title}
                    >
                      {epiProps.noFilterResultsCtaButton.text}
                    </a>
                  )}
                </div>
              </div>
            )}

            <div className='stack-md'>
              {/* Show 'reset filters' button when filters are applied and there are results */}
              {filtersApplied && programsToDisplay.length > 0 && (
                <button className="btn-tertiary" onClick={handleClearAllFilters}>
                  {epiProps.clearFiltersButtonText}
                </button>
              )}

              {/* View All CTA - only show when in specialty view (one card displaying based on query string) */}
              {(showingSpecialtyView || showDirectDeliveryPrograms) && (
                <button className="btn-tertiary" onClick={handleViewAllPrograms}>
                  {epiProps.showAllProgramsCTAText}
                </button>
              )}

              {/* Display programs */}
              {
                programsToDisplay && programsToDisplay.length > 0 &&
                programsToDisplay.map((p, index) => (
                  <ProgramCard
                    key={`${p.programCode}-${index}`}
                    program={p}
                    isSelected={userSelections.userProgramSelection?.programCode === p.programCode && userSelections.userProgramSelection?.isRenewal === p.isRenewal && userSelections.userProgramSelection?.isDirectDelivery === p.isDirectDelivery}
                    programType={'specialty'}
                    showPrice={eventRegistrationRefreshModel.showPrice}
                    currency={eventRegistrationRefreshModel.currency}
                    onSelect={() => handleProgramSelect(p)}
                    onCheckRenewalEligibility={handleCheckRenewalEligibility}
                  />
                ))
              }
            </div>

          </MainContent>
          <SideBarContent>
            <ProgramSummary
              userSelections={userSelections}
              onContinue={handleContinue}
              showValidationMessage={showValidationMessage}
              validationMessage={epiProps.programStepValidationMessage}
            />
          </SideBarContent>
        </ContentWrapper>

        <Modal
          additionalClassName="app-modal-eligibility"
          size='large'
          isOpen={(specialtyCodeToCheckRenewalEligibilityFor && specialtyCodeToCheckRenewalEligibilityFor.length > 0) === true}
          onClose={() => setSpecialtyCodeToCheckRenewalEligibilityFor("")}
          title={getRenewalSpecialtyProgramName(specialtyCodeToCheckRenewalEligibilityFor)}>
          <div>
            <RenewalEligibility specialtyCode={specialtyCodeToCheckRenewalEligibilityFor} />
          </div>
        </Modal>

        {/*Compare Tool*/}
        <Modal
          size='fullscreen'
          isOpen={showCompareTool}
          additionalClassName="app-modal-compare"
          onClose={() => setShowCompareTool(false)}
          title={epiProps.compareProgramsModalHeadingText}>
          <div>
            <CompareTool
              programs={allProgramsForCurrentToggle}
            />
          </div>
        </Modal>

      </div>
    )
  }

  return null;
}